import { apiFetch, healthCheck } from './api';
import { db } from './db';
import { emitirDatosCambiados } from './events';
import {
  cancelarNotificacionDePago,
  cancelarNotificacionDeTarea,
  cancelarNotificacionesDeCita,
  programarNotificacionPago,
  programarNotificacionTarea,
  programarNotificaciones,
} from './notifications';
import { contar, listarPendientes, marcarFallo, marcarSincronizado } from './outbox';
import { getAccessToken } from './tokenStorage';
import type { Cita, Cliente, Idea, OutboxItem, Pago, Proyecto, Tarea } from '../types';

const META_KEY_LAST_PULL = 'sync.lastPull';
const INTERVALO_MS = 30_000;
const BATCH_MAX = 500;

export type EstadoSync = 'idle' | 'offline' | 'syncing' | 'error';

type ListenerEstado = (estado: EstadoSync, pendientes: number) => void;

const listenersEstado = new Set<ListenerEstado>();

export function onEstadoSync(fn: ListenerEstado): () => void {
  listenersEstado.add(fn);
  return () => {
    listenersEstado.delete(fn);
  };
}

let estadoActual: EstadoSync = 'idle';
let pendientesActual = 0;

async function actualizarEstado(estado: EstadoSync): Promise<void> {
  estadoActual = estado;
  pendientesActual = await contar();
  listenersEstado.forEach((fn) => fn(estadoActual, pendientesActual));
}

interface SyncPushResultItem {
  id: string;
  status: 'ok' | 'conflict' | 'error';
  error?: string;
}

// Shapes que devuelve realmente el server (Prisma): usan createdAt y NO incluyen
// los campos desnormalizados cliente/telefono en la cita. El cliente local
// usa creado/creada y necesita los campos desnormalizados para el render.
// Por eso normalizamos antes de bulkPut.
interface ClienteServerRow {
  id: string;
  nombre: string;
  telefono: string;
  email: string | null;
  notas: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

interface CitaServerRow {
  id: string;
  clienteId: string | null;
  nombre: string | null;
  telefono: string | null;
  fecha: string;
  hora: string;
  notas: string | null;
  notificacionesSilenciadas: boolean;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

interface PagoServerRow {
  id: string;
  destinatario: string;
  monto: number;
  fechaLimite: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

interface TareaServerRow {
  id: string;
  titulo: string;
  fechaLimite: string | null;
  notas: string | null;
  hecha: boolean;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

interface ProyectoServerRow {
  id: string;
  nombre: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

interface IdeaServerRow {
  id: string;
  proyectoId: string;
  titulo: string;
  notas: string | null;
  hecha: boolean;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

interface SyncPullResponse {
  clientes: ClienteServerRow[];
  citas: CitaServerRow[];
  pagos: PagoServerRow[];
  tareas: TareaServerRow[];
  proyectos: ProyectoServerRow[];
  ideas: IdeaServerRow[];
  deletedClientes: string[];
  deletedCitas: string[];
  deletedPagos: string[];
  deletedTareas: string[];
  deletedProyectos: string[];
  deletedIdeas: string[];
  serverTime: string;
}

function normalizarCliente(row: ClienteServerRow): Cliente {
  return {
    id: row.id,
    nombre: row.nombre,
    telefono: row.telefono,
    email: row.email ?? '',
    notas: row.notas ?? '',
    creado: row.createdAt,
    updatedAt: row.updatedAt,
    deletedAt: row.deletedAt,
  };
}

function normalizarCita(row: CitaServerRow, mapClientes: Map<string, Cliente>): Cita {
  const cl = row.clienteId ? mapClientes.get(row.clienteId) : null;
  const fecha =
    typeof row.fecha === 'string' && row.fecha.includes('T') ? row.fecha.split('T')[0] : row.fecha;
  // Si la cita esta linkeada a un cliente, usamos el join (nombre actualizado).
  // Si es ad-hoc, usamos los snapshots nombre/telefono que viven en la cita.
  return {
    id: row.id,
    clienteId: row.clienteId,
    cliente: cl?.nombre ?? row.nombre ?? '',
    telefono: cl?.telefono ?? row.telefono ?? '',
    fecha,
    hora: row.hora,
    notas: row.notas ?? '',
    notificacionesSilenciadas: row.notificacionesSilenciadas,
    creada: row.createdAt,
    updatedAt: row.updatedAt,
    deletedAt: row.deletedAt,
  };
}

function normalizarPago(row: PagoServerRow): Pago {
  return {
    id: row.id,
    destinatario: row.destinatario,
    monto: Number(row.monto),
    fechaLimite: row.fechaLimite,
    creado: row.createdAt,
    updatedAt: row.updatedAt,
    deletedAt: row.deletedAt,
  };
}

function normalizarTarea(row: TareaServerRow): Tarea {
  return {
    id: row.id,
    titulo: row.titulo,
    fechaLimite: row.fechaLimite ?? null,
    notas: row.notas ?? '',
    hecha: row.hecha,
    creada: row.createdAt,
    updatedAt: row.updatedAt,
    deletedAt: row.deletedAt,
  };
}

function normalizarProyecto(row: ProyectoServerRow): Proyecto {
  return {
    id: row.id,
    nombre: row.nombre,
    creado: row.createdAt,
    updatedAt: row.updatedAt,
    deletedAt: row.deletedAt,
  };
}

function normalizarIdea(row: IdeaServerRow): Idea {
  return {
    id: row.id,
    proyectoId: row.proyectoId,
    titulo: row.titulo,
    notas: row.notas ?? '',
    hecha: row.hecha,
    creada: row.createdAt,
    updatedAt: row.updatedAt,
    deletedAt: row.deletedAt,
  };
}

async function obtenerLastPull(): Promise<string | undefined> {
  const row = await db.meta.get(META_KEY_LAST_PULL);
  return typeof row?.value === 'string' ? row.value : undefined;
}

async function setLastPull(iso: string): Promise<void> {
  await db.meta.put({ key: META_KEY_LAST_PULL, value: iso });
}

async function pushOutbox(items: OutboxItem[]): Promise<void> {
  if (items.length === 0) return;
  const operations = items.slice(0, BATCH_MAX).map((it) => ({
    type: it.type,
    entity: it.entity,
    id: it.id,
    data: it.data ?? undefined,
    clientUpdatedAt: it.clientUpdatedAt,
  }));

  const resp = await apiFetch<{ results: SyncPushResultItem[] }>('/sync/push', {
    method: 'POST',
    body: { operations },
  });

  for (let i = 0; i < resp.results.length; i++) {
    const original = items[i];
    const resultado = resp.results[i];
    if (!original.autoId) continue;
    if (resultado.status === 'ok') {
      await marcarSincronizado(original.autoId);
    } else {
      await marcarFallo(original.autoId, resultado.error ?? resultado.status);
    }
  }
}

async function pullCambios(): Promise<void> {
  const since = await obtenerLastPull();
  const resp = await apiFetch<SyncPullResponse>('/sync/pull', {
    query: { since },
  });

  const clientesNormalizados = resp.clientes.map(normalizarCliente);
  const pagosNormalizados = resp.pagos.map(normalizarPago);
  const tareasNormalizadas = resp.tareas.map(normalizarTarea);
  const proyectosNormalizados = resp.proyectos.map(normalizarProyecto);
  const ideasNormalizadas = resp.ideas.map(normalizarIdea);
  let citasNormalizadas: Cita[] = [];

  const tablas = [db.clientes, db.citas, db.pagos, db.tareas, db.proyectos, db.ideas, db.meta];
  await db.transaction('rw', tablas, async () => {
    if (clientesNormalizados.length > 0) await db.clientes.bulkPut(clientesNormalizados);

    if (resp.citas.length > 0) {
      // Después de meter los clientes nuevos, leer todos para resolver el join
      // nombre/teléfono en cada cita. La denormalización vive en el cliente para
      // que la tarjeta de cita renderice sin tener que mirar otra tabla.
      const todosClientes = await db.clientes.toArray();
      const mapClientes = new Map(todosClientes.map((c) => [c.id, c]));
      citasNormalizadas = resp.citas.map((c) => normalizarCita(c, mapClientes));
      await db.citas.bulkPut(citasNormalizadas);
    }

    if (pagosNormalizados.length > 0) await db.pagos.bulkPut(pagosNormalizados);
    if (tareasNormalizadas.length > 0) await db.tareas.bulkPut(tareasNormalizadas);
    if (proyectosNormalizados.length > 0) await db.proyectos.bulkPut(proyectosNormalizados);
    if (ideasNormalizadas.length > 0) await db.ideas.bulkPut(ideasNormalizadas);

    const ahora = new Date().toISOString();
    for (const id of resp.deletedClientes) {
      await db.clientes.update(id, (c) => {
        c.deletedAt = ahora;
        c.updatedAt = ahora;
      });
    }
    for (const id of resp.deletedCitas) {
      await db.citas.update(id, (c) => {
        c.deletedAt = ahora;
        c.updatedAt = ahora;
      });
    }
    for (const id of resp.deletedPagos) {
      await db.pagos.update(id, (p) => {
        p.deletedAt = ahora;
        p.updatedAt = ahora;
      });
    }
    for (const id of resp.deletedTareas) {
      await db.tareas.update(id, (t) => {
        t.deletedAt = ahora;
        t.updatedAt = ahora;
      });
    }
    for (const id of resp.deletedProyectos) {
      await db.proyectos.update(id, (p) => {
        p.deletedAt = ahora;
        p.updatedAt = ahora;
      });
    }
    for (const id of resp.deletedIdeas) {
      await db.ideas.update(id, (i) => {
        i.deletedAt = ahora;
        i.updatedAt = ahora;
      });
    }
    await db.meta.put({ key: META_KEY_LAST_PULL, value: resp.serverTime });
  });

  await setLastPull(resp.serverTime);

  // Programar/cancelar notificaciones locales segun lo que llego del server.
  // Asi un recordatorio creado en web suena en el celular cuando el usuario abre
  // la app (sync corre cada 30s o al abrir). En web es no-op.
  await sincronizarNotificacionesLocales(citasNormalizadas, resp.deletedCitas);
  await sincronizarNotificacionesPagos(pagosNormalizados, resp.deletedPagos);
  await sincronizarNotificacionesTareas(tareasNormalizadas, resp.deletedTareas);

  if (
    resp.clientes.length > 0 ||
    resp.citas.length > 0 ||
    resp.pagos.length > 0 ||
    resp.tareas.length > 0 ||
    resp.proyectos.length > 0 ||
    resp.ideas.length > 0 ||
    resp.deletedClientes.length > 0 ||
    resp.deletedCitas.length > 0 ||
    resp.deletedPagos.length > 0 ||
    resp.deletedTareas.length > 0 ||
    resp.deletedProyectos.length > 0 ||
    resp.deletedIdeas.length > 0
  ) {
    emitirDatosCambiados();
  }
}

async function sincronizarNotificacionesLocales(
  citasNuevas: Cita[],
  citasBorradas: string[]
): Promise<void> {
  for (const id of citasBorradas) {
    try {
      await cancelarNotificacionesDeCita(id);
    } catch (e) {
      console.warn('No se pudo cancelar notificacion de cita borrada', id, e);
    }
  }
  for (const cita of citasNuevas) {
    if (cita.deletedAt) continue;
    if (cita.notificacionesSilenciadas) continue;
    try {
      await programarNotificaciones(cita, { silencioso: true });
    } catch (e) {
      console.warn('No se pudo programar notificacion de cita pulleada', cita.id, e);
    }
  }
}

async function sincronizarNotificacionesPagos(
  pagosNuevos: Pago[],
  pagosBorrados: string[]
): Promise<void> {
  for (const id of pagosBorrados) {
    try {
      await cancelarNotificacionDePago(id);
    } catch (e) {
      console.warn('No se pudo cancelar notificacion de pago borrado', id, e);
    }
  }
  for (const pago of pagosNuevos) {
    if (pago.deletedAt) continue;
    try {
      await programarNotificacionPago(pago, { silencioso: true });
    } catch (e) {
      console.warn('No se pudo programar notificacion de pago pulleado', pago.id, e);
    }
  }
}

async function sincronizarNotificacionesTareas(
  tareasNuevas: Tarea[],
  tareasBorradas: string[]
): Promise<void> {
  for (const id of tareasBorradas) {
    try {
      await cancelarNotificacionDeTarea(id);
    } catch (e) {
      console.warn('No se pudo cancelar notificacion de tarea borrada', id, e);
    }
  }
  for (const tarea of tareasNuevas) {
    if (tarea.deletedAt) continue;
    try {
      // programarNotificacionTarea ya cancela si no tiene fecha o está hecha.
      await programarNotificacionTarea(tarea, { silencioso: true });
    } catch (e) {
      console.warn('No se pudo programar notificacion de tarea pulleada', tarea.id, e);
    }
  }
}

let syncEnProgreso = false;

export async function sincronizar(): Promise<void> {
  if (syncEnProgreso) return;
  if (!getAccessToken()) {
    await actualizarEstado('idle');
    return;
  }

  syncEnProgreso = true;
  try {
    const haRed = await healthCheck();
    if (!haRed) {
      await actualizarEstado('offline');
      return;
    }

    await actualizarEstado('syncing');

    const pendientes = await listarPendientes();
    if (pendientes.length > 0) {
      await pushOutbox(pendientes);
    }
    await pullCambios();
    await actualizarEstado('idle');
  } catch (e) {
    console.error('Sync falló:', e);
    await actualizarEstado('error');
  } finally {
    syncEnProgreso = false;
  }
}

let timerId: ReturnType<typeof setInterval> | null = null;
let listenerOnline: (() => void) | null = null;
let listenerVisibility: (() => void) | null = null;

export function arrancarSyncWorker(): void {
  if (timerId !== null) return;

  void sincronizar();
  timerId = setInterval(() => {
    void sincronizar();
  }, INTERVALO_MS);

  listenerOnline = () => {
    void sincronizar();
  };
  listenerVisibility = () => {
    if (document.visibilityState === 'visible') {
      void sincronizar();
    }
  };
  window.addEventListener('online', listenerOnline);
  document.addEventListener('visibilitychange', listenerVisibility);
}

export function detenerSyncWorker(): void {
  if (timerId !== null) {
    clearInterval(timerId);
    timerId = null;
  }
  if (listenerOnline) {
    window.removeEventListener('online', listenerOnline);
    listenerOnline = null;
  }
  if (listenerVisibility) {
    document.removeEventListener('visibilitychange', listenerVisibility);
    listenerVisibility = null;
  }
}

export function estadoSyncActual(): { estado: EstadoSync; pendientes: number } {
  return { estado: estadoActual, pendientes: pendientesActual };
}
